import React from "react";
import {
  UserPlus,
  Filter,
  FileCheck2,
  IndianRupee,
  TrendingUp,
  Sparkles,
} from "lucide-react";

const steps = [
  {
    id: "01",
    title: "Onboard",
    description: "Complete a quick KYC and set your lending preferences.",
    icon: <UserPlus size={20} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: "02",
    title: "Receive Leads",
    description: "Get pre-verified leads matched to your lending criteria.",
    icon: <Filter size={20} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: "03",
    title: "Review & Approve",
    description: "Evaluate applications and approve loans with confidence.",
    icon: <FileCheck2 size={20} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: "04",
    title: "Disburse",
    description: "Disburse loans seamlessly through our secure platform.",
    icon: <IndianRupee size={20} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: "05",
    title: "Track & Reconcile",
    description: "Monitor performance and reconcile payouts effortlessly.",
    icon: <TrendingUp size={20} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
];

function LendersOnboarding() {
  return (
    <section className="relative overflow-hidden bg-white py-14 sm:py-18 lg:py-20">
      {/* Soft Ambient Radial Background Glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(46,229,162,0.06),transparent_50%)]"
      />

      <div className="section-container relative z-10">
        
        {/* ================= SECTION HEADER ================= */}
        <div className="mx-auto max-w-[740px] text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hero-green-light)] px-3.5 py-1 font-jakarta text-[12px] font-bold text-[var(--hero-green)] border border-[var(--hero-green-border)] shadow-xs mb-3.5">
            <Sparkles size={13} className="text-[var(--hero-green)]" />
            <span>HOW IT WORKS</span>
          </span>

          <h2 className="font-bricolage text-[30px] sm:text-[38px] md:text-[44px] font-bold leading-[1.1] tracking-[-1px] text-[#111827]">
            From lead to disbursal, <span className="text-[var(--hero-green)]">simplified</span>
          </h2>

          <p className="mt-2.5 font-jakarta text-[14.5px] sm:text-[16px] leading-[1.6] text-[#556369]">
            A streamlined 5-step flow engineered for maximum lender efficiency and speed.
          </p>
        </div>

        {/* ================= DESKTOP HORIZONTAL 5-STEP FLOW ================= */}
        <div className="relative mt-10 sm:mt-13 lg:mt-15 hidden md:block">
          
          {/* Continuous Animated Connecting SVG Line (Starts at center of Node 1 and ends at Node 5) */}
          <div className="absolute top-[32px] lg:top-[34px] left-[10%] right-[10%] -translate-y-1/2 z-0">
            <svg
              className="w-full h-[6px] overflow-visible"
              viewBox="0 0 1000 6"
              fill="none"
              preserveAspectRatio="none"
            >
              {/* Background static guide line */}
              <line
                x1="0"
                y1="3"
                x2="1000"
                y2="3"
                stroke="#e2eae5"
                strokeWidth="2"
                strokeDasharray="5 5"
              />

              {/* Foreground Animated Continuous Flowing Line */}
              <line
                x1="0"
                y1="3"
                x2="1000"
                y2="3"
                stroke="#008751"
                strokeWidth="2"
                strokeDasharray="5 5"
                strokeLinecap="round"
                className="step-flow-line"
              />
            </svg>
          </div>

          {/* 5 Step Nodes Grid */}
          <div className="grid grid-cols-5 gap-3 lg:gap-5 relative z-10">
            {steps.map((step) => (
              <div key={step.id} className="flex flex-col items-center text-center group">
                
                {/* Sleek Compact Circle Node */}
                <div className="relative flex h-[64px] w-[64px] lg:h-[68px] lg:w-[68px] shrink-0 aspect-square items-center justify-center rounded-full bg-white border border-[#d6e8dd] shadow-[0_6px_20px_rgba(0,0,0,0.04)] transition-all duration-300 group-hover:scale-108 group-hover:border-[var(--hero-green)] group-hover:shadow-[0_12px_26px_rgba(0,135,81,0.14)]">
                  
                  {/* Hover Aura */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 rounded-full bg-[var(--hero-green)]/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  />

                  {/* Icon */}
                  <div className="relative z-10 transition-transform duration-300 group-hover:scale-105">
                    {step.icon}
                  </div>
                </div>

                {/* Step Number Badge */}
                <span className="mt-3 font-jakarta text-[11px] font-bold text-[var(--hero-green)] uppercase tracking-wider">
                  {step.id}
                </span>

                {/* Step Title */}
                <h3 className="mt-1 font-bricolage text-[16px] lg:text-[17px] font-bold tracking-tight text-[#111827] group-hover:text-[var(--hero-green)] transition-colors duration-200">
                  {step.title}
                </h3>

                {/* Step Description */}
                <p className="mt-1 font-jakarta text-[12.5px] leading-[1.5] text-[#556369] max-w-[175px]">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ================= MOBILE & TABLET EXPANDED STEP FLOW ================= */}
        <div className="mt-10 sm:mt-12 space-y-0 md:hidden relative max-w-[420px] mx-auto px-2">
          {steps.map((step, idx) => (
            <div
              key={step.id}
              className="relative flex items-start gap-5 pb-9 last:pb-0"
            >
              {/* Connector Line strictly between current step and next step (Zero Overshoot) */}
              {idx < steps.length - 1 && (
                <div className="absolute left-[31px] top-[60px] bottom-0 w-[3px] -translate-x-1/2 z-0 overflow-hidden pointer-events-none">
                  <svg
                    className="w-full h-full"
                    viewBox="0 0 3 100"
                    preserveAspectRatio="none"
                  >
                    {/* Background guide line */}
                    <line
                      x1="1.5"
                      y1="0"
                      x2="1.5"
                      y2="100"
                      stroke="#e2eae5"
                      strokeWidth="2"
                      strokeDasharray="6 6"
                      vectorEffect="non-scaling-stroke"
                    />
                    {/* Flowing animated green line */}
                    <line
                      x1="1.5"
                      y1="0"
                      x2="1.5"
                      y2="100"
                      stroke="#008751"
                      strokeWidth="2.5"
                      strokeDasharray="6 6"
                      vectorEffect="non-scaling-stroke"
                      className="step-flow-line-vert"
                    />
                  </svg>
                </div>
              )}

              {/* Icon Circle (Guaranteed 1:1 Aspect Ratio) */}
              <div className="relative z-10 flex h-[60px] w-[60px] shrink-0 aspect-square items-center justify-center rounded-[20px] bg-white border border-[#d6e8dd] shadow-[0_6px_20px_rgba(0,135,81,0.08)]">
                {step.icon}
                {/* Step number badge */}
                <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--hero-green)] text-[10px] font-bold font-jakarta text-white shadow-xs">
                  {step.id}
                </span>
              </div>

              {/* Text Info */}
              <div className="pt-1.5 min-w-0">
                <h3 className="font-bricolage text-[16.5px] sm:text-[17.5px] font-bold text-[#111827]">
                  {step.title}
                </h3>
                <p className="mt-1 font-jakarta text-[13px] sm:text-[13.5px] leading-[1.5] text-[#556369]">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Line Flow Animation Keyframes */}
      <style>{`
        @keyframes flowHorizontal {
          from {
            stroke-dashoffset: 40;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        @keyframes flowVertical {
          from {
            stroke-dashoffset: 40;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        .step-flow-line {
          animation: flowHorizontal 1.8s linear infinite;
        }
        .step-flow-line-vert {
          animation: flowVertical 1.8s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .step-flow-line,
          .step-flow-line-vert {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}

export default LendersOnboarding;
